import {
  Badge,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  RefreshButton,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdArrowBack, MdAutoAwesome } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import {
  AgentJobDetail,
  AgentJobEvent,
  agentJobsService,
} from "services/agentJobsService";
import { getThemeColor } from "theme/colors";

const STATUS_COLOR: Record<string, string> = {
  Completed: "green",
  Failed: "red",
  Cancelled: "orange",
  Running: "blue",
  WaitingForApproval: "red",
  WaitingForInput: "purple",
  Skipped: "orange",
  Pending: "gray",
  Planned: "gray",
  Paused: "gray",
  Approved: "green",
  Denied: "red",
  Expired: "orange",
};

/** One line per event, from the same payloads the widget renders live. */
const describeEvent = (evt: AgentJobEvent): string | null => {
  const payload = evt.payload as any;
  switch (evt.type) {
    case "ProgressStep": {
      const step = payload?.step;
      if (!step) return null;
      return `${step.label ?? step.type}${step.meta?.reason ? ` — ${step.meta.reason}` : ""}`;
    }
    case "JobStatus":
      return `${payload?.status ?? ""}${payload?.reason ? ` — ${payload.reason}` : ""}`;
    case "SubAgentPlanReady":
      return Array.isArray(payload?.agents)
        ? payload.agents.map((a: any) => a.description ?? a.label).join(" · ")
        : null;
    case "JobApprovalRequired":
      return Array.isArray(payload?.approvals)
        ? payload.approvals.map((a: any) => a.stepLabel ?? a.tools).join(", ")
        : null;
    case "StepTimedOut":
      return payload?.label ?? null;
    default:
      return null;
  }
};

/** How many record ids an approval row lists before "+N more". */
const MAX_LISTED_IDS = 8;

/** "101, 103, 107 +4 more" — the records an approval acts on, kept to one line. */
const formatTargetIds = (ids: string[]): string => {
  const shown = ids.slice(0, MAX_LISTED_IDS).join(", ");
  return ids.length > MAX_LISTED_IDS
    ? `${shown} +${ids.length - MAX_LISTED_IDS}`
    : shown;
};

/** Read-only job page for tenant staff (WP-42): steps, approvals, events, answer. */
const AgentJobDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AgentJobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setDetail(await agentJobsService.get(id));
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.agentJobs.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const job = detail?.job;

  return (
    <Flex direction="column" w="full" gap={5} minW={0}>
      <Flex justify="space-between" align="center">
        <HStack>
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/agent-jobs")}
            size="sm"
          >
            <MdArrowBack /> {t("app.common.back")}
          </Button>
          <MdAutoAwesome size={28} color={getThemeColor("icon").base} />
          <Heading
            size="xl"
            fontWeight="bold"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {job?.title ?? t("app.agentJobs.title")}
          </Heading>
          {job && (
            <Badge
              colorPalette={STATUS_COLOR[job.status] ?? "gray"}
              variant="subtle"
            >
              {t(`app.agentJobs.status.${job.status}`)}
            </Badge>
          )}
        </HStack>
        <RefreshButton onClick={load} loading={loading} />
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text>{error}</Text>
        </InfoBox>
      )}

      {job && (
        <>
          <Card.Root>
            <Card.Body>
              <VStack align="stretch" gap={1} fontSize="sm">
                <Text>
                  <b>{t("app.agentJobs.triggerHeader")}:</b>{" "}
                  {t(`app.agentJobs.trigger.${job.trigger}`)}
                </Text>
                <Text>
                  <b>{t("app.agentJobs.owner")}:</b>{" "}
                  {job.ownerCustomerId ?? "—"}
                </Text>
                {job.sourceKind !== null && (
                  <Text>
                    <b>{t("app.agentJobs.sourceHeader")}:</b>{" "}
                    {t(`app.agentJobs.source.${job.sourceKind}`, {
                      defaultValue: String(job.sourceKind),
                    })}
                  </Text>
                )}
                {job.statusReason && (
                  <Text>
                    <b>{t("app.agentJobs.statusReason")}:</b>{" "}
                    {job.status === "Paused" && job.statusReason === "budget"
                      ? t("app.agentJobs.pausedForBudget")
                      : job.statusReason}
                  </Text>
                )}
                <Text>
                  <b>{t("app.agentJobs.tokens")}:</b>{" "}
                  {job.inputTokens + job.outputTokens > 0
                    ? t("app.agentJobs.tokensValue", {
                        input: job.inputTokens.toLocaleString(),
                        output: job.outputTokens.toLocaleString(),
                      })
                    : "—"}
                </Text>
                <Text>
                  <b>{t("app.agentJobs.cost")}:</b>{" "}
                  {job.costUsd > 0 ? `$${job.costUsd.toFixed(4)}` : "—"}
                </Text>
                <Text>
                  <b>{t("app.agentJobs.createdAt")}:</b>{" "}
                  {new Date(job.createdAt).toLocaleString()}
                </Text>
                {job.chatSessionId && (
                  <Text fontFamily="mono" fontSize="xs">
                    {t("app.agentJobs.session")}: {job.chatSessionId}
                  </Text>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {detail?.output && (
            <Card.Root>
              <Card.Header>
                <Heading size="md">{t("app.agentJobs.output")}</Heading>
              </Card.Header>
              <Card.Body>
                <Text whiteSpace="pre-wrap" fontSize="sm">
                  {detail.output}
                </Text>
              </Card.Body>
            </Card.Root>
          )}

          {detail && detail.steps.length > 0 && (
            <Card.Root>
              <Card.Header>
                <Heading size="md">{t("app.agentJobs.steps")}</Heading>
              </Card.Header>
              <Card.Body p={0} overflowX="auto">
                <Table.Root variant="outline" size="sm" w="full">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.statusHeader")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.stepKind")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.stepLabel")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.attempts")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.dependsOn")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.lastError")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {detail.steps.map((step) => (
                      <Table.Row key={step.id}>
                        <Table.Cell>
                          <Badge
                            colorPalette={STATUS_COLOR[step.status] ?? "gray"}
                            variant="subtle"
                          >
                            {step.status}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>{step.kind}</Table.Cell>
                        <Table.Cell>{step.label}</Table.Cell>
                        <Table.Cell>
                          {step.attempt}/{step.maxAttempts}
                          {step.maxIterations != null &&
                          (step.iteration > 1 || step.maxIterations > 1)
                            ? ` · ${t("app.agentJobs.iterationOf", {
                                n: step.iteration,
                                m: step.maxIterations,
                              })}`
                            : step.iteration > 1
                              ? ` · ${t("app.agentJobs.iteration")} ${step.iteration}`
                              : ""}
                        </Table.Cell>
                        <Table.Cell>
                          {step.dependsOn.join(", ") || "—"}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs" color="red.500">
                            {step.lastError ?? ""}
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Card.Body>
            </Card.Root>
          )}

          {detail && detail.approvals.length > 0 && (
            <Card.Root>
              <Card.Header>
                <Heading size="md">{t("app.agentJobs.approvals")}</Heading>
              </Card.Header>
              <Card.Body p={0} overflowX="auto">
                <Table.Root variant="outline" size="sm" w="full">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.statusHeader")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.stepLabel")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.tool")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.risk")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.requestedAt")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.agentJobs.decidedBy")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {detail.approvals.map((a) => (
                      <Table.Row key={a.id}>
                        <Table.Cell>
                          <Badge
                            colorPalette={STATUS_COLOR[a.status] ?? "gray"}
                            variant="subtle"
                          >
                            {a.status}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {a.stepLabel ?? "—"}
                          {a.targets?.map((target) => (
                            <Text
                              key={`${target.from}:${target.field}`}
                              fontSize="xs"
                              color="fg.muted"
                            >
                              {t("app.agentJobs.actsOn", {
                                ids: formatTargetIds(target.ids),
                              })}
                            </Text>
                          ))}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontFamily="mono" fontSize="xs">
                            {a.toolName}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>{a.riskLevel}</Table.Cell>
                        <Table.Cell>
                          {new Date(a.requestedAt).toLocaleString()}
                        </Table.Cell>
                        <Table.Cell>
                          {a.decidedBy ?? "—"}
                          {a.decidedAt
                            ? ` · ${new Date(a.decidedAt).toLocaleString()}`
                            : ""}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Card.Body>
            </Card.Root>
          )}

          {detail && detail.events.length > 0 && (
            <Card.Root>
              <Card.Header>
                <Heading size="md">{t("app.agentJobs.events")}</Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="stretch" gap={1} maxH="400px" overflowY="auto">
                  {detail.events.map((evt, i) => {
                    const text = describeEvent(evt);
                    if (!text) return null;
                    return (
                      <HStack
                        key={`${evt.sequence}-${i}`}
                        fontSize="xs"
                        align="start"
                      >
                        <Text
                          fontFamily="mono"
                          color={{ base: "gray.500", _dark: "gray.400" }}
                          flexShrink={0}
                        >
                          {new Date(evt.at).toLocaleTimeString()}
                        </Text>
                        <Text
                          fontFamily="mono"
                          color={{ base: "gray.500", _dark: "gray.400" }}
                          flexShrink={0}
                        >
                          {evt.type}
                        </Text>
                        <Text truncate>{text}</Text>
                      </HStack>
                    );
                  })}
                </VStack>
              </Card.Body>
            </Card.Root>
          )}
        </>
      )}
    </Flex>
  );
};

export default AgentJobDetails;
